import { z } from "zod";

/**
 * The shape of a form, shared by the collaborative draft, the published
 * version, and everything that validates or renders either.
 *
 * A question's `id` is minted once and never reused: every answer ever given
 * points at it, so it survives rewording, retyping, reordering and republishing.
 */

export const QUESTION_TYPES = [
  "short_answer",
  "money",
  "paragraph",
  "repeatable_group",
  "multiple_choice",
  "checkboxes",
  "dropdown",
  "date",
  "time",
  "file_upload",
  "linear_scale",
  "rating",
  "grid",
  "text_block",
] as const;

export const questionTypeSchema = z.enum(QUESTION_TYPES);
export type QuestionType = z.infer<typeof questionTypeSchema>;

/** Comparisons a `visibleWhen` condition or a section jump can make. */
export const conditionOpSchema = z.enum([
  "equals",
  "notEquals",
  "contains",
  "isAnswered",
  "isEmpty",
]);
export type ConditionOp = z.infer<typeof conditionOpSchema>;

export const conditionSchema = z.object({
  questionId: z.string(),
  op: conditionOpSchema,
  /** Unused by `isAnswered` / `isEmpty`. */
  value: z.string().optional(),
  /**
   * What `value` holds: one of the source question's option ids, the literal
   * text an answer is compared against, or — for `"other"` — nothing at all:
   * the condition asks whether the applicant took the question's "Other" box,
   * which has no option id to name.
   *
   * Without it the two are indistinguishable, and the source question's
   * current type is not a reliable stand-in: an admin can retype a choice
   * question to text while another admin is concurrently writing a condition
   * on its options, and the merged draft then carries an option id compared
   * against whatever the applicant types — matching nothing, and silently
   * hiding the question it guards.
   *
   * Optional only for conditions written before it existed; every read
   * populates it, so anything reaching a publish check carries one.
   */
  compare: z.enum(["option", "text", "other"]).optional(),
});
export type Condition = z.infer<typeof conditionSchema>;

/**
 * The smallest value each integer-valued rule accepts. The schema below is
 * built from this map, and the builder's rule inputs and the publish check
 * read it too — one set of bounds, nowhere for a second copy to drift.
 */
export const INTEGER_RULE_MINIMUMS = {
  minLength: 0,
  maxLength: 1,
  minSelected: 0,
  maxSelected: 1,
} as const;

/** Validation rules a question can carry. Which apply depends on its type. */
export const validationRuleSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("minLength"),
    value: z.number().int().min(INTEGER_RULE_MINIMUMS.minLength),
  }),
  z.object({
    kind: z.literal("maxLength"),
    value: z.number().int().min(INTEGER_RULE_MINIMUMS.maxLength),
  }),
  z.object({ kind: z.literal("email") }),
  z.object({ kind: z.literal("url") }),
  z.object({
    kind: z.literal("regex"),
    pattern: z.string(),
    negate: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal("contains"),
    value: z.string(),
    negate: z.boolean().optional(),
  }),
  z.object({ kind: z.literal("number") }),
  z.object({ kind: z.literal("integer") }),
  z.object({ kind: z.literal("min"), value: z.number() }),
  z.object({ kind: z.literal("max"), value: z.number() }),
  z.object({
    kind: z.literal("minSelected"),
    value: z.number().int().min(INTEGER_RULE_MINIMUMS.minSelected),
  }),
  z.object({
    kind: z.literal("maxSelected"),
    value: z.number().int().min(INTEGER_RULE_MINIMUMS.maxSelected),
  }),
]);
export type ValidationRule = z.infer<typeof validationRuleSchema>;

/**
 * What each rule is called wherever an admin sees one — the builder's rule
 * picker and the publish errors both read from here, so a refused rule is
 * named with the exact words the admin added it under.
 */
export const VALIDATION_RULE_LABELS: Record<ValidationRule["kind"], string> = {
  minLength: "Minimum characters",
  maxLength: "Maximum characters",
  email: "Must be an email address",
  url: "Must be a web address",
  regex: "Must match a pattern",
  contains: "Must contain text",
  number: "Must be a number",
  integer: "Must be a whole number",
  min: "Minimum value",
  max: "Maximum value",
  minSelected: "Fewest that can be ticked",
  maxSelected: "Most that can be ticked",
};

/**
 * One option a choice question offers.
 *
 * The `id` is what an answer points at, for the same reason a question's id is:
 * the label is editable, and an admin fixing a typo in "Recieve updates" must
 * not orphan everyone who already picked it. Ordered by `pos` like everything
 * else, so two admins reordering options merge instead of duplicating them.
 */
export const questionOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Fractional index. See `sortKey.ts`. */
  pos: z.string(),
});
export type QuestionOption = z.infer<typeof questionOptionSchema>;

/** The pictures a rating question can count in. */
export const ratingIconSchema = z.enum(["star", "heart", "thumb"]);
export type RatingIcon = z.infer<typeof ratingIconSchema>;

/** Per-type settings. Every field optional, so a type change never invalidates a question. */
export const scalarQuestionConfigSchema = z.object({
  entryLabel: z.string().optional(),
  minEntries: z.number().optional(),
  maxEntries: z.number().optional(),
  options: z.array(questionOptionSchema).optional(),
  allowOther: z.boolean().optional(),
  /**
   * Show the options in a different random order to each applicant. Stored
   * intent for the applicant-facing renderer; the builder always shows the
   * authored order, because that's the order being edited.
   */
  shuffle: z.boolean().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  minLabel: z.string().optional(),
  maxLabel: z.string().optional(),
  /**
   * File upload: the content types an applicant may attach. Omitted means any
   * type — a constraint on authoring, never the security boundary (files are
   * always served as attachments regardless of type).
   */
  acceptedTypes: z.array(z.string()).optional(),
  /**
   * File upload: per-file size limit. Omitted means the registry default —
   * `maxFileSizeMbOf` is the one place that decides what absence means, so
   * nothing compares against `undefined`.
   */
  maxFileSizeMb: z.number().optional(),
  /**
   * File upload: how many files one answer may hold. Omitted means one. The
   * answer is a list either way, so raising this later changes no stored
   * shape.
   */
  maxFiles: z.number().optional(),
  /**
   * A grid's rows. Same shape as options — each row has an id answers point
   * at, so retitling a row never orphans what was answered on it. The grid's
   * columns live in `options`, which is what lets a choice question become a
   * grid (and back) without reminting the ids its answers reference.
   */
  rows: z.array(questionOptionSchema).optional(),
  /** A grid where each row takes any number of columns, not exactly one. */
  multiSelect: z.boolean().optional(),
  /** A date question that also asks for a time of day. */
  includeTime: z.boolean().optional(),
  /** A date question that asks only for a month and day, no year. */
  monthDayOnly: z.boolean().optional(),
  /** A time question that asks for an amount of time, not a time of day. */
  duration: z.boolean().optional(),
  ratingIcon: ratingIconSchema.optional(),
});
/** Group children use the same field settings and validation as standalone questions. */
export const REPEAT_FIELD_TYPES = [
  "short_answer",
  "money",
  "paragraph",
  "multiple_choice",
  "checkboxes",
  "dropdown",
  "date",
  "time",
  "file_upload",
  "linear_scale",
  "rating",
  "grid",
  "text_block",
] as const;
export const repeatFieldSchema = questionOptionSchema.extend({
  type: z.enum(REPEAT_FIELD_TYPES),
  required: z.boolean(),
  help: z.string().optional(),
  shortDescription: z.string().optional(),
  config: scalarQuestionConfigSchema.optional(),
  validation: z.array(validationRuleSchema).optional(),
  visibleWhen: conditionSchema.optional(),
});
export type RepeatField = z.infer<typeof repeatFieldSchema>;
export const questionConfigSchema = scalarQuestionConfigSchema.extend({
  fields: z.array(repeatFieldSchema).optional(),
});
export type QuestionConfig = z.infer<typeof questionConfigSchema>;

export const questionSchema = z.object({
  id: z.string(),
  sectionId: z.string(),
  groupId: z.string().optional(),
  type: questionTypeSchema,
  label: z.string(),
  /** Optional preparation summary shown in the application guide. */
  shortDescription: z.string().optional(),
  help: z.string().optional(),
  required: z.boolean(),
  /** Fractional index. See `sortKey.ts`. */
  pos: z.string(),
  config: questionConfigSchema.optional(),
  validation: z.array(validationRuleSchema).optional(),
  /** Ask this question only when the condition holds. */
  visibleWhen: conditionSchema.optional(),
});
export type Question = z.infer<typeof questionSchema>;

/**
 * A section jump: when `when` holds at the end of this section, go to
 * `toSectionId` instead of the next one in order. `null` means submit.
 */
export const sectionJumpSchema = z.object({
  when: conditionSchema,
  toSectionId: z.string().nullable(),
});
export type SectionJump = z.infer<typeof sectionJumpSchema>;

export const sectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  pos: z.string(),
  jumps: z.array(sectionJumpSchema).optional(),
  /** Where to go when no jump matched. `null` submits; omitted means "next in order". */
  defaultNextSectionId: z.string().nullable().optional(),
  visibleWhen: conditionSchema.optional(),
});
export type Section = z.infer<typeof sectionSchema>;

/** Repetition is independent of the fields a group contains. */
export const repeatSchema = z.object({
  entryLabel: z.string().optional(),
  minEntries: z.number().int().min(0).max(100).default(0),
  maxEntries: z.number().int().min(1).max(100).default(100),
});
export type Repeat = z.infer<typeof repeatSchema>;

/** Groups only organize existing questions; their identities and rules stay intact. */
export const questionGroupSchema = z.object({
  id: z.string(),
  sectionId: z.string(),
  title: z.string(),
  description: z.string().optional(),
  repeat: repeatSchema.optional(),
});
export type QuestionGroup = z.infer<typeof questionGroupSchema>;

export const formContentSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  sections: z.array(sectionSchema),
  questions: z.array(questionSchema),
  groups: z.array(questionGroupSchema).optional(),
});
/** A whole form — the unit that gets published as a version. */
export type FormContent = z.infer<typeof formContentSchema>;

/**
 * The shape of stored content, stamped with the schema that wrote it.
 *
 * A published version is a JSON blob that has to stay readable for as long as
 * any answer points at it — years, across every future change to the shapes
 * above. Without a stamp, code reading an old blob can only sniff its fields
 * and guess, and guessing wrong either rejects a published form outright or
 * silently reads it as something it isn't. With one, `parseVersionContent`
 * knows which migration to run.
 *
 * It lives inside the content rather than in a column of its own so that
 * `planPublish` — a pure function with no database access — is the thing that
 * sets it. A column would be a second place to remember, and forgetting is the
 * failure this exists to prevent.
 */
export const CONTENT_SCHEMA_VERSION = 5;

/**
 * Every stamp this build knows how to read. Version 1 lacked the grid, rating
 * and text-block types and the settings that came with them; everything it
 * could express is still expressible, so reading it is just accepting the
 * older stamp. Version 2 lacks optional question short descriptions; version 3
 * lacks repeatable-group answers. See
 * `parseVersionContent`.
 */
export const READABLE_SCHEMA_VERSIONS = [
  1,
  2,
  3,
  4,
  CONTENT_SCHEMA_VERSION,
] as const;

export const publishedContentSchema = formContentSchema.extend({
  schemaVersion: z.literal(CONTENT_SCHEMA_VERSION),
});
export type PublishedContent = z.infer<typeof publishedContentSchema>;

/**
 * One selection on a choice question: an option, or free text when the
 * question allows "other".
 *
 * A union rather than a nullable option id, so "picked option 3" and "typed
 * something else" can't be confused for one another — the whole point of
 * option ids is that a stored answer is unambiguous about what it refers to.
 */
export const choiceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("option"), optionId: z.string() }),
  z.object({ kind: z.literal("other"), value: z.string() }),
]);
export type Choice = z.infer<typeof choiceSchema>;

/**
 * One row of a grid answer: which columns were picked on that row, by id on
 * both sides. Only answered rows are stored — an unanswered row is absent,
 * never present with an empty list, so "how much of the grid was answered" has
 * one representation.
 */
export const gridRowAnswerSchema = z.object({
  rowId: z.string(),
  optionIds: z.array(z.string()).min(1),
});
export type GridRowAnswer = z.infer<typeof gridRowAnswerSchema>;

/**
 * One attached file, as an answer refers to it: the id of the file record
 * that owns the stored object, plus the name to show. Never a path — the path
 * lives on the record alone, so a replacement can't leave two copies of it
 * disagreeing.
 */
export const fileRefSchema = z.object({
  fileId: z.string(),
  filename: z.string(),
});
export type FileRef = z.infer<typeof fileRefSchema>;

/** Repeats wrap ordinary answers. Nested repeats are deliberately not part of this schema. */
export const scalarAnswerSchema = z.union([
  z.object({ kind: z.literal("text"), value: z.string() }),
  z.object({ kind: z.literal("number"), value: z.number() }),
  z.object({ kind: z.literal("choice"), choice: choiceSchema }),
  z.object({ kind: z.literal("choices"), choices: z.array(choiceSchema) }),
  z.object({ kind: z.literal("files"), files: z.array(fileRefSchema) }),
  z.object({ kind: z.literal("grid"), rows: z.array(gridRowAnswerSchema) }),
]);
export type ScalarAnswer = z.infer<typeof scalarAnswerSchema>;

/** Strings remain readable for forms published before typed repeated fields existed. */
export const repeatedEntrySchema = z.object({
  id: z.string().min(1).max(100),
  values: z.record(z.string(), z.union([z.string(), scalarAnswerSchema])),
});
export type RepeatedEntry = z.infer<typeof repeatedEntrySchema>;
export const repeatableAnswerSchema = z.object({
  kind: z.literal("entries"),
  entries: z
    .array(repeatedEntrySchema)
    .refine(
      (entries) =>
        new Set(entries.map((entry) => entry.id)).size === entries.length,
      "Each entry must have a unique identity.",
    ),
});
export function entryValue(
  entry: RepeatedEntry,
  fieldId: string,
): ScalarAnswer | undefined {
  const value = entry.values[fieldId];
  return typeof value === "string" ? { kind: "text", value } : value;
}
export function isBlankScalarAnswer(value: ScalarAnswer | undefined): boolean {
  if (!value) return true;
  switch (value.kind) {
    case "text":
      return !value.value.trim();
    case "number":
      return false;
    case "choice":
      return value.choice.kind === "other" && !value.choice.value.trim();
    case "choices":
      return value.choices.every(
        (choice) => choice.kind === "other" && !choice.value.trim(),
      );
    case "files":
      return !value.files.length;
    case "grid":
      return !value.rows.length;
  }
}
export function isBlankEntry(entry: RepeatedEntry): boolean {
  return Object.keys(entry.values).every((id) =>
    isBlankScalarAnswer(entryValue(entry, id)),
  );
}
export const answerValueSchema = z.union([
  repeatableAnswerSchema,
  scalarAnswerSchema,
]);
export type AnswerValue = z.infer<typeof answerValueSchema>;
/** Answers keyed by question ID (or repeat group ID), never labels or array positions. */
export type AnswerMap = Record<string, AnswerValue | undefined>;
